import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiClient } from "../../../../api/client";
import { url } from "../../../../api";
import { toast } from "react-toastify";
import { FaComments, FaPaperPlane } from "react-icons/fa";
import emojiRegex from 'emoji-regex';
import GraphemeSplitter from 'grapheme-splitter';
import { useAotdCardPlay } from '../../../respectCards/aotd/AotdCardPlayContext';
import EmojiKeyboard from '../../../emojis/components/EmojiKeyboard';

interface Comment {
  _id: string;
  aotdId: string;
  // userId and username are nulled out by the backend for anonymous comments
  // (see routes/art/artOfTheDay.ts GET /comments) — never trust them to be
  // populated when isAnonymous is true.
  userId: string | null;
  username: string | null;
  emoji: string;
  text: string;
  isAuthor: boolean;
  pinnedUntil?: string | null;
  pinnedByCardSlug?: string | null;
  highlightedUntil?: string | null;
  highlightedByCardSlug?: string | null;
  isAnonymous?: boolean;
  anonymizedByCardSlug?: string | null;
  createdAt: string;
}

interface AOTDCommentsProps {
  aotdId: string | null;
  currentUsername: string | null;
  isAuthenticated: boolean;
}

// relative time formatting
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "now";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString();
};

const INITIAL_DISPLAY_COUNT = 3;

const splitter = new GraphemeSplitter();

const countEmojis = (text: string): number => {
  const graphemes = splitter.splitGraphemes(text);
  const regex = emojiRegex();
  return graphemes.filter(g => {
    // emoji-regex is /g — reset lastIndex per grapheme or .test() resumes
    // mid-string and undercounts repeated emojis ("\u2764\uFE0F\u2764\uFE0F" counted as 1)
    regex.lastIndex = 0;
    return regex.test(g);
  }).length;
};

const AOTDComments: React.FC<AOTDCommentsProps> = ({ aotdId, currentUsername, isAuthenticated }) => {
  // P5 cards overhaul: the Anonymous-card hint routes through the AOTD page's
  // shared Play modal (AotdCardPlayProvider owns the hand read and the modal),
  // so this component no longer runs its own /my-cards query.
  const { openCardPlay, anonymousCardInHand } = useAotdCardPlay();

  const [comments, setComments] = useState<Comment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [hasCommented, setHasCommented] = useState(false);

  // input state - single text input (emoji is extracted by backend)
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emojiError, setEmojiError] = useState<string>("");
  // emoji keyboard (keyboard-swap UX): the trigger blurs the input first so
  // the phone's OS keyboard slides away and our keyboard takes its place;
  // picking an emoji refocuses the input, which brings the OS keyboard back.
  const [emojiKbOpen, setEmojiKbOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // last known caret position — captured on blur/select so the emoji can be
  // inserted at the caret even after the input loses focus to the keyboard
  const lastCaretRef = useRef<number | null>(null);
  // Phase 2 cards-launch: the user-driven "post anonymous" toggle is
  // gone. Anonymity is now exclusively card-driven — the user plays an
  // Anonymous card on the AOTD context and the next comment they post
  // is anonymized server-side via `comment.pre_post` →
  // `comment_anonymize`. The hint below points users to the Anonymous
  // card flow when they own one in their hand.

  const fetchComments = useCallback(async (offset = 0, append = false) => {
    if (!aotdId) return;

    try {
      setLoading(true);
      const headers = {};
      const response = await apiClient.get(
        `${url}/api/art-of-the-day/comments/${aotdId}?limit=50&offset=${offset}`,
        headers
      );

      if (response.data.success) {
        const {
          comments: fetchedComments,
          total: totalCount,
          hasCommented: alreadyCommented,
        } = response.data.data;

        if (append) {
          setComments(prev => [...prev, ...fetchedComments]);
        } else {
          setComments(fetchedComments);
        }
        setTotal(totalCount);
        setHasCommented(alreadyCommented || false);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setLoading(false);
    }
  }, [aotdId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async () => {
    const trimmedText = text.trim();

    // clear previous errors
    setEmojiError("");

    if (!trimmedText) {
      toast.error("Comment cannot be empty");
      return;
    }

    if (trimmedText.length > 45) {
      toast.error("Comment must be 45 characters or less");
      return;
    }

    // validate exactly one emoji
    const emojiCount = countEmojis(trimmedText);
    if (emojiCount === 0) {
      setEmojiError("Comment must contain exactly one emoji");
      return;
    }
    if (emojiCount > 1) {
      setEmojiError(`Comment must contain exactly one emoji (found ${emojiCount})`);
      return;
    }

    try {
      setSubmitting(true);
      const headers = {};
      const response = await apiClient.post(
        `${url}/api/art-of-the-day/comments`,
        { aotdId, text: trimmedText },
        headers
      );

      if (response.data.success) {
        setText("");
        setEmojiError("");
        setHasCommented(true);
        await fetchComments();
        toast.success("Comment added!");
      }
    } catch (error: any) {
      console.error("Error posting comment:", error);
      const errorMsg = error.response?.data?.error || "Failed to post comment";
      toast.error(errorMsg);

      // if conflict error, user already commented
      if (error.response?.status === 409) {
        setHasCommented(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmojiTrigger = () => {
    if (emojiKbOpen) {
      setEmojiKbOpen(false);
      inputRef.current?.focus();
      return;
    }
    // blur FIRST so the OS keyboard closes, then ours opens in its place
    inputRef.current?.blur();
    setEmojiKbOpen(true);
  };

  const handleEmojiPick = (emoji: string) => {
    // insert at the last known caret position (fall back to append)
    const insertionIndex = Math.min(lastCaretRef.current ?? text.length, text.length);
    const next = text.slice(0, insertionIndex) + emoji + text.slice(insertionIndex);
    if (next.length > 45) {
      toast.error("Comment must be 45 characters or less");
      return;
    }
    setText(next);
    setEmojiError("");
    // the keyboard auto-closes on pick; refocus to bring the OS keyboard back
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        const pos = insertionIndex + emoji.length;
        el.setSelectionRange(pos, pos);
        lastCaretRef.current = pos;
      }
    });
  };

  const displayedComments = showAll ? comments : comments.slice(0, INITIAL_DISPLAY_COUNT);
  const remainingCount = total - INITIAL_DISPLAY_COUNT;

  // hide section if no aotdId, or if not authenticated and no comments
  if (!aotdId) return null;
  if (!isAuthenticated && comments.length === 0 && !loading) return null;

  return (
    <div>
      {/* header with beta tag */}
      <div className="flex items-center gap-2 text-gray-600 mb-2">
        <FaComments className="text-sm" />
        <span className="text-sm font-medium">Comments</span>
        {total > 0 && <span className="text-sm text-gray-400">({total})</span>}
        <span className="text-[10px] font-semibold bg-indigo-500 text-white px-1.5 py-0.5 rounded">
          BETA
        </span>
      </div>

      {/* comment input - only for authenticated users who haven't commented yet */}
      {isAuthenticated && !hasCommented && (
        <div className="mb-3">
          {/* relative wrapper anchors the desktop emoji dropdown (left-0 top-full);
              on mobile the keyboard portals into a bottom sheet instead */}
          <div className="flex gap-2 relative">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  lastCaretRef.current = e.target.selectionStart;
                  setEmojiError(""); // clear error on input change
                }}
                onSelect={(e) => {
                  lastCaretRef.current = e.currentTarget.selectionStart;
                }}
                onBlur={(e) => {
                  lastCaretRef.current = e.currentTarget.selectionStart;
                }}
                placeholder="Include exactly one emoji..."
                className="w-full h-9 px-3 pr-12 text-sm border border-gray-300 rounded focus:outline-none focus:border-blue-400"
                maxLength={45}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !submitting && text.trim()) {
                    handleSubmit();
                  }
                }}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                {text.length}/45
              </span>
            </div>
            <button
              type="button"
              title="Pick an emoji"
              aria-label="Pick an emoji"
              // preventDefault so tapping the trigger doesn't flash focus
              // changes — the blur/refocus dance is owned by handleEmojiTrigger
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleEmojiTrigger}
              disabled={submitting}
              className="h-9 w-9 flex items-center justify-center rounded text-base opacity-70 hover:opacity-100 hover:bg-gray-100 transition-all cursor-pointer"
            >
              😊
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !text.trim()}
              className={`h-9 w-9 flex items-center justify-center rounded ${submitting || !text.trim()
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600 cursor-pointer"
                } text-white transition-colors`}
            >
              {submitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FaPaperPlane className="text-sm" />
              )}
            </button>
            {/* custom emoji keyboard — auto-closes on pick (stayOpen not set) */}
            <EmojiKeyboard
              open={emojiKbOpen}
              onClose={() => setEmojiKbOpen(false)}
              onPick={handleEmojiPick}
              mode="free"
              title="Add an emoji"
            />
          </div>
          {/* Card-driven anonymity hint. Renders only when the user owns an
              in-hand Anonymous card; opens the page's shared Play modal. */}
          {anonymousCardInHand ? (
            <button
              type="button"
              onClick={() => openCardPlay(anonymousCardInHand)}
              className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline cursor-pointer"
              disabled={submitting}
            >
              🕶️ You own an Anonymous card. Tap to play it on this comment to post anonymously.
            </button>
          ) : null}
          {/* inline error message */}
          {emojiError && (
            <div className="mt-1 text-xs text-red-600">
              {emojiError}
            </div>
          )}
        </div>
      )}

      {/* comments list */}
      {loading && comments.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-2">Loading comments...</div>
      ) : comments.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-2">
          {isAuthenticated ? "Be the first to comment!" : "No comments yet"}
        </div>
      ) : (
        <div className="space-y-2">
          {displayedComments.map((comment) => (
            <div
              key={comment._id}
              className={`flex items-start gap-2 rounded-md px-2 py-1.5 text-sm ${
                comment.highlightedUntil && new Date(comment.highlightedUntil).getTime() > Date.now()
                  ? "bg-amber-50 ring-1 ring-amber-200"
                  : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <span className="text-gray-700">{comment.text}</span>
                <span className="text-gray-400 ml-1">
                  - {comment.isAnonymous ? "Anonymous" : comment.username}
                  {comment.pinnedUntil && new Date(comment.pinnedUntil).getTime() > Date.now() && (
                    <span className="ml-1 text-[10px] font-medium bg-amber-100 text-amber-700 px-1 py-0.5 rounded">
                      Pinned
                    </span>
                  )}
                  {comment.highlightedUntil && new Date(comment.highlightedUntil).getTime() > Date.now() && (
                    <span className="ml-1 text-[10px] font-medium bg-yellow-100 text-yellow-700 px-1 py-0.5 rounded">
                      Spotlight
                    </span>
                  )}
                  {comment.isAnonymous && (
                    <span
                      className="ml-1 text-[10px] font-medium bg-slate-100 text-slate-700 px-1 py-0.5 rounded"
                      title="Posted anonymously"
                    >
                      🕶️ Anonymous
                    </span>
                  )}
                  {/* don't match null===null when the comment is anonymous —
                      the server strips username, so "You" must compare against
                      a real string. */}
                  {comment.username && currentUsername === comment.username && (
                    <span className="ml-1 text-[10px] font-medium bg-blue-100 text-blue-700 px-1 py-0.5 rounded">
                      You
                    </span>
                  )}
                  {comment.isAuthor && (
                    <span className="ml-1 text-[10px] font-medium bg-amber-100 text-amber-700 px-1 py-0.5 rounded">
                      Author
                    </span>
                  )}
                  <span className="ml-1">• {getRelativeTime(comment.createdAt)}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* show more button */}
      {!showAll && remainingCount > 0 && (
        <button
          onClick={() => setShowAll(true)}
          className="mt-2 text-sm text-blue-500 hover:text-blue-600 hover:underline w-full text-center"
        >
          Show more ({remainingCount})
        </button>
      )}

      {/* show less button */}
      {showAll && comments.length > INITIAL_DISPLAY_COUNT && (
        <button
          onClick={() => setShowAll(false)}
          className="mt-2 text-sm text-gray-500 hover:text-gray-600 hover:underline w-full text-center"
        >
          Show less
        </button>
      )}

    </div>
  );
};

export default AOTDComments;
